package io.horion.cv.components.preview

import androidx.compose.runtime.Composable
import io.horion.cv.api.datasource.cv.OrionCV
import org.jetbrains.compose.web.dom.*

private const val SIDEBAR_BG     = "#001228"
private const val MAIN_BLUE      = "#1a2744"
private const val LABEL_GRAY     = "#aaaaaa"
private const val BAR_TRACK      = "#2a3a5c"
private const val BAR_FILL       = "#5b8def"
private const val CITY_GRAY      = "#888888"
private const val TEXT_DARK      = "#222222"
private const val WHITE          = "#ffffff"
private const val BORDER_SECTION = "#ffffffb3"


@Composable
fun CvTwoPreview(data: OrionCV) {

    val pi = data.personalInformation
    val validPersonalizedSections =
        data.personalizedSections.filter { section ->
            section.resume.isNotBlank() ||
                    section.started.isNotBlank() ||
                    section.ended.isNotBlank() ||
                    section.description.isNotBlank()
        }

    // ── FILTRAGE DES DONNÉES ──────────────────────────────────────────────────
    val filteredCompetences = data.competences.filter { it.name.isNotBlank() }
    val filteredLanguages   = data.languages.filter { it.language.name.isNotBlank() }
    val filteredQualities   = data.qualities.filter { it.isNotBlank() }
    val filteredSoftware    = data.software.filter { it.name.isNotBlank() }
    val filteredHobbies     = data.hobbies.filter { it.isNotBlank() }

    val filteredFormations = data.formations.filter {
        it.label.isNotBlank() || it.fieldOfStudy?.isNotBlank() == true || it.description.isNotBlank()
    }
    val filteredExperiences = data.experiences.filter {
        it.post.isNotBlank() || it.enterprise.isNotBlank() || it.description.any { desc -> desc.isNotBlank() }
    }
    val filteredStages = data.stages.filter {
        it.post.isNotBlank() || it.enterprise.isNotBlank() || it.description.isNotBlank()
    }
    val filteredLearns = data.learns.filter {
        it.label.isNotBlank() || it.description.isNotBlank()
    }
    val filteredActivities = data.extracurricularActivities.filter {
        it.name.isNotBlank() || it.enterprise.isNotBlank() || it.description.isNotBlank()
    }
    val filteredCertificates = data.certificates.filter {
        it.name.isNotBlank() || it.description.isNotBlank()
    }
    val filteredAchievements = data.achievements.filter { it.isNotBlank() }
    val filteredReferences   = data.references.filter { it.name.isNotBlank() }

    Style {
        """
    .cv-two-preview,
    .cv-two-preview * {
        box-sizing: border-box;
    }

    .cv-two-preview p,
    .cv-two-preview div,
    .cv-two-preview span,
    .cv-two-preview a,
    .cv-two-preview li {
        max-width: 100%;
        min-width: 0;
        white-space: normal;
        overflow-wrap: anywhere;
        word-break: break-word;
    }

    .cv-two-preview img {
        max-width: 100%;
        height: auto;
    }
    """.trimIndent()
    }

    Div(attrs = {
        classes("cv-two-preview")
        style {
            property("display", "flex")
            property("width", "794px")
            property("min-height", "1123px")
            property("font-family", "Inter, 'Segoe UI', Arial, sans-serif")
            property("font-size", "11px")
            property("background", WHITE)
            property("color", TEXT_DARK)
            property("box-sizing", "border-box")
            property("overflow", "hidden")
        }
    }) {

        // ── SIDEBAR GAUCHE ─────────────────────────────────────────────────────
        Div(attrs = {
            style {
                property("width", "240px")
                property("min-width", "240px")
                property("max-width", "240px")
                property("min-height", "1123px")
                property("background-color", SIDEBAR_BG)
                property("color", WHITE)
                property("padding", "24px 18px")
                property("flex-shrink", "0")
                property("box-sizing", "border-box")
                property("display", "flex")
                property("flex-direction", "column")
                property("overflow", "hidden")
            }
        }) {

            // Photo ronde
            if (pi.photo.isNotBlank()) {
                Div(attrs = { style {
                    property("width", "90px")
                    property("height", "90px")
                    property("border-radius", "50%")
                    property("overflow", "hidden")
                    property("margin", "0 auto 18px")
                    property("border", "2px solid #ffffff44")
                    property("flex-shrink", "0")
                }}) {
                    Img(src = pi.photo, attrs = { style {
                        property("width", "100%")
                        property("height", "100%")
                        property("object-fit", "cover")
                    }})
                }
            }

            // INFORMATIONS PERSONNELLES
            val hasBirth   = pi.birth.isNotBlank()
            val hasNat     = pi.nationality.isNotBlank()
            val hasMarital = pi.maritalStatus.isNotBlank()
            val hasLicense = pi.drivingLicense.isNotBlank()
            if (hasBirth || hasNat || hasMarital || hasLicense) {
                SbTitle2("INFORMATIONS", "PERSONNELLES")
                SbDivider()
                if (hasBirth)   SbLabelRow("Naiss. :", pi.birth)
                if (hasNat)     SbLabelRow("Nat. :", pi.nationality)
                if (hasMarital) SbLabelRow("Sit. fam. :", pi.maritalStatus)
                if (hasLicense) SbLabelRow("Permis :", pi.drivingLicense)
            }

            // CONTACTS
            val hasPhone    = pi.phone.isNotBlank()
            val hasEmail    = pi.email.isNotBlank()
            val hasAddress  = pi.address.isNotBlank()
            val hasLocation = !hasAddress && (pi.city.isNotBlank() || pi.state.isNotBlank() || pi.country.isNotBlank())
            val hasWebsite  = pi.siteWeb?.second?.isNotBlank() == true
            val hasLinks    = pi.links.any { it.second.isNotBlank() }
            if (hasPhone || hasEmail || hasAddress || hasLocation || hasWebsite || hasLinks) {
                SbTitle2("CONTACTS")
                SbDivider()
                if (hasPhone)   SbLabelRow("Tél. :", pi.phone)
                if (hasEmail)   SbLabelRow("Email :", pi.email)
                if (hasAddress) SbLabelRow("Addr. :", pi.address)
                if (hasLocation) {
                    val location = listOfNotNull(
                        pi.city.takeIf { it.isNotBlank() },
                        pi.state.takeIf { it.isNotBlank() },
                        pi.country.takeIf { it.isNotBlank() }
                    ).joinToString(", ")
                    SbLabelRow("Ville :", location)
                }
                pi.siteWeb?.let { (_, url) ->
                    if (url.isNotBlank()) SbLinkRow(null, url)
                }
                pi.links.forEach { (label, url) ->
                    if (url.isNotBlank()) SbLinkRow(label.ifBlank { "Lien" } + " :", url)
                }
            }

            // AUTRES COMPÉTENCES
            if (filteredCompetences.isNotEmpty()) {
                SbTitle2("AUTRES COMPÉTENCES")
                SbDivider()
                filteredCompetences.forEach { comp ->
                    SbSkillBar(comp.name, comp.level.num)
                }
            }

            // LANGUES
            if (filteredLanguages.isNotEmpty()) {
                SbTitle2("LANGUES")
                SbDivider()
                filteredLanguages.forEach { lang ->
                    Div(attrs = { style {
                        property("color", WHITE)
                        property("font-size", "11px")
                        property("font-weight", "700")
                        property("letter-spacing", "1px")
                        property("margin-bottom", "5px")
                    }}) { Text(lang.language.name) }
                }
            }

            // QUALITÉS
            if (filteredQualities.isNotEmpty()) {
                SbTitle2("QUALITÉS")
                SbDivider()
                SbChipList(filteredQualities)
            }

            // LOGICIELS
            if (filteredSoftware.isNotEmpty()) {
                SbTitle2("LOGICIELS")
                SbDivider()
                filteredSoftware.forEach { sw -> SbSoftwareItem(sw.name, sw.stars) }
            }

            // HOBBIES
            if (filteredHobbies.isNotEmpty()) {
                SbTitle2("HOBBIES")
                SbDivider()
                SbChipList(filteredHobbies)
            }
        }

        // ── CONTENU PRINCIPAL ──────────────────────────────────────────────────
        Div(attrs = {
            style {
                property("flex", "1")
                property("width", "0")
                property("max-width", "100%")
                property("min-width", "0")
                property("padding", "28px 28px 24px")
                property("background", WHITE)
                property("box-sizing", "border-box")
                property("overflow", "hidden")
            }
        }) {

            // Nom + poste
            val hasName = pi.firstName.isNotBlank() || pi.lastName.isNotBlank()
            if (hasName) {
                Div(
                    attrs = {
                        style {
                            property("width", "100%")
                            property("max-width", "100%")
                            property("min-width", "0")
                            property("box-sizing", "border-box")
                            property("font-size", "26px")
                            property("font-weight", "700")
                            property("color", MAIN_BLUE)
                            property("text-transform", "uppercase")
                            property("line-height", "1.2")
                            property("white-space", "normal")
                            property("overflow-wrap", "anywhere")
                            property("word-break", "break-word")
                        }
                    }) {
                    Text("${pi.firstName} ${pi.lastName}".trim()) }
            }
            if (pi.bp.isNotBlank()) {
                Div(attrs = { style {
                    property("width", "100%")
                    property("max-width", "100%")
                    property("min-width", "0")
                    property("box-sizing", "border-box")
                    property("font-size", "13px")
                    property("color", "#666666")
                    property("text-transform", "uppercase")
                    property("letter-spacing", "2px")
                    property("line-height", "1.3")
                    property("margin-top", "4px")
                    property("margin-bottom", "12px")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                }}) { Text(pi.bp) }
            }

            // Profil texte
            if (!data.profil.isNullOrBlank()) {
                HtmlBlock(data.profil) {
                    property("font-size", "11px")
                    property("color", "#444444")
                    property("line-height", "1.65")
                    property("text-align", "justify")
                    property("margin-bottom", "18px")
                }
            }

            // RUBRIQUES PERSONNALISÉES
            if (validPersonalizedSections.isNotEmpty()) {
                validPersonalizedSections.forEach { section ->
                    Cv2PersonalizedSection(section)
                }
            }

            // FORMATIONS
            if (filteredFormations.isNotEmpty()) {
                MainTitle2("FORMATIONS")
                Div(attrs = { style { property("margin-top", "8px") }}) {
                    filteredFormations.forEachIndexed { index, f ->
                        Cv2TimelineItem(
                            period      = formatPeriod(f.started, f.ended),
                            title       = f.label,
                            subtitle    = f.fieldOfStudy ?: "",
                            city        = "",
                            bullets     = if (f.description.isNotBlank()) listOf(f.description) else emptyList(),
                            isLast      = index == filteredFormations.lastIndex
                        )
                    }
                }
            }

            // EXPÉRIENCES PROFESSIONNELLES
            if (filteredExperiences.isNotEmpty()) {
                MainTitle2("EXPÉRIENCES PROFESSIONNELLES")
                Div(attrs = { style { property("margin-top", "8px") }}) {
                    filteredExperiences.forEachIndexed { index, exp ->
                        Cv2TimelineItem(
                            period   = formatPeriod(exp.started, exp.ended),
                            title    = exp.post,
                            subtitle = exp.enterprise,
                            city     = exp.city,
                            bullets  = exp.description.filter { it.isNotBlank() },
                            isLast   = index == filteredExperiences.lastIndex
                        )
                    }
                }
            }

            // STAGES
            if (filteredStages.isNotEmpty()) {
                MainTitle2("STAGES")
                Div(attrs = { style { property("margin-top", "8px") }}) {
                    filteredStages.forEachIndexed { index, stage ->
                        Cv2TimelineItem(
                            period   = formatPeriod(stage.started, stage.ended),
                            title    = stage.post,
                            subtitle = stage.enterprise,
                            city     = stage.city,
                            bullets  = if (stage.description.isNotBlank()) listOf(stage.description) else emptyList(),
                            isLast   = index == filteredStages.lastIndex
                        )
                    }
                }
            }

            // COURS
            if (filteredLearns.isNotEmpty()) {
                MainTitle2("COURS")
                Div(attrs = { style { property("margin-top", "8px") }}) {
                    filteredLearns.forEachIndexed { index, learn ->
                        Cv2TimelineItem(
                            period   = formatPeriod(learn.started, learn.ended),
                            title    = learn.label,
                            subtitle = "",
                            city     = "",
                            bullets  = if (learn.description.isNotBlank()) listOf(learn.description) else emptyList(),
                            isLast   = index == filteredLearns.lastIndex
                        )
                    }
                }
            }

            // ACTIVITÉS EXTRA-SCOLAIRES
            if (filteredActivities.isNotEmpty()) {
                MainTitle2("ACTIVITÉS EXTRA-SCOLAIRES")
                Div(attrs = { style { property("margin-top", "8px") }}) {
                    filteredActivities.forEachIndexed { index, activity ->
                        Cv2TimelineItem(
                            period   = formatPeriod(activity.started, activity.ended),
                            title    = activity.name,
                            subtitle = activity.enterprise,
                            city     = activity.city,
                            bullets  = if (activity.description.isNotBlank()) listOf(activity.description) else emptyList(),
                            isLast   = index == filteredActivities.lastIndex
                        )
                    }
                }
            }

            // CERTIFICATS
            if (filteredCertificates.isNotEmpty()) {
                MainTitle2("CERTIFICATS")
                Div(attrs = { style { property("margin-top", "8px") }}) {
                    filteredCertificates.forEachIndexed { index, cert ->
                        Cv2TimelineItem(
                            period   = extractYear(cert.date),
                            title    = cert.name,
                            subtitle = "",
                            city     = "",
                            bullets  = if (cert.description.isNotBlank()) listOf(cert.description) else emptyList(),
                            isLast   = index == filteredCertificates.lastIndex
                        )
                    }
                }
            }

            // RÉALISATIONS
            if (filteredAchievements.isNotEmpty()) {
                MainTitle2("RÉALISATIONS")
                Div(attrs = { style { property("margin-top", "8px"); property("margin-bottom", "4px") }}) {
                    SimpleBulletList(filteredAchievements)
                }
            }

            // RÉFÉRENCES
            if (filteredReferences.isNotEmpty()) {
                MainTitle2("RÉFÉRENCES")
                Div(attrs = { style { property("margin-top", "8px") }}) {
                    filteredReferences.forEach { ref -> ReferenceCard(ref) }
                }
            }

            // SIGNATURE
            data.signature?.let { sig ->
                if (sig.city.isNotBlank() || sig.date.isNotBlank() || sig.image.isNotBlank()) {
                    SignatureBlock(sig)
                }
            }
        }
    }
}

// ── Sidebar : titre de section ─────────────────────────────────────────────────

@Composable
private fun SbTitle2(vararg lines: String) {
    Div(attrs = { style {
        property("margin-top", "18px")
        property("margin-bottom", "6px")
    }}) {
        lines.forEach { line ->
            Div(attrs = { style {
                property("color", WHITE)
                property("font-size", "11px")
                property("font-weight", "700")
                property("letter-spacing", "0.8px")
                property("line-height", "1.4")
            }}) { Text(line) }
        }
    }
}

// ── Sidebar : ligne séparatrice ────────────────────────────────────────────────

@Composable
private fun SbDivider() {
    Div(attrs = { style {
        property("border-bottom", "2px solid $BORDER_SECTION")
        property("margin-bottom", "8px")
    }})
}

// ── Sidebar : ligne label + valeur ────────────────────────────────────────────

@Composable
private fun SbLabelRow(label: String, value: String) {
    Div(attrs = {
        style {
            property("display", "flex")
            property("width", "100%")
            property("max-width", "100%")
            property("min-width", "0")
            property("gap", "8px")
            property("margin-bottom", "6px")
            property("font-size", "10px")
            property("align-items", "flex-start")
        }
    }) {
        Span(attrs = {
            style {
                property("width", "42px")
                property("min-width", "42px")
                property("flex-shrink", "0")
                property("color", LABEL_GRAY)
            }
        }) {
            Text(label)
        }

        Span(attrs = {
            style {
                property("width", "0")
                property("flex", "1")
                property("min-width", "0")
                property("max-width", "100%")
                property("color", WHITE)
                property("line-height", "1.4")
                property("white-space", "normal")
                property("overflow-wrap", "anywhere")
                property("word-break", "break-word")
            }
        }) {
            Text(value)
        }
    }
}

// ── Sidebar : ligne label + lien cliquable ────────────────────────────────────

@Composable
private fun SbLinkRow(label: String?, url: String) {
    Div(attrs = {
        style {
            property("display", "flex")
            property("width", "100%")
            property("max-width", "100%")
            property("min-width", "0")
            property("gap", "8px")
            property("margin-bottom", "6px")
            property("font-size", "10px")
            property("align-items", "flex-start")
        }
    }) {
        if (!label.isNullOrBlank()) {
            Span(attrs = {
                style {
                    property("width", "42px")
                    property("min-width", "42px")
                    property("flex-shrink", "0")
                    property("color", LABEL_GRAY)
                }
            }) {
                Text(label)
            }
        }

        A(
            href = url,
            attrs = {
                attr("target", "_blank")
                attr("rel", "noopener noreferrer")

                style {
                    property("width", "0")
                    property("flex", "1")
                    property("min-width", "0")
                    property("max-width", "100%")
                    property("color", WHITE)
                    property("line-height", "1.4")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                    property("text-decoration", "underline")
                }
            }
        ) {
            Text(url)
        }
    }
}

// ── Sidebar : barre de compétence ─────────────────────────────────────────────

@Composable
private fun SbSkillBar(name: String, level: Float) {
    Div(attrs = {
        style {
            property("width", "100%")
            property("max-width", "100%")
            property("min-width", "0")
            property("margin-bottom", "9px")
        }
    }) {
        Div(attrs = {
            style {
                property("width", "100%")
                property("max-width", "100%")
                property("min-width", "0")
                property("color", WHITE)
                property("font-size", "10px")
                property("font-weight", "700")
                property("letter-spacing", "1px")
                property("line-height", "1.3")
                property("margin-bottom", "3px")
                property("white-space", "normal")
                property("overflow-wrap", "anywhere")
                property("word-break", "break-word")
            }
        }) {
            Text(name)
        }

        Div(attrs = {
            style {
                property("width", "100%")
                property("max-width", "100%")
                property("height", "5px")
                property("background", BAR_TRACK)
                property("border-radius", "3px")
                property("overflow", "hidden")
            }
        }) {
            Div(attrs = {
                style {
                    property("height", "5px")
                    property("max-width", "100%")
                    property("background", BAR_FILL)
                    property("border-radius", "3px")
                    property("width", "${(level * 100).toInt().coerceIn(0, 100)}%")
                }
            })
        }
    }
}

// ── Sidebar : liste de puces (hobbies, qualités) ──────────────────────────────

@Composable
private fun SbChipList(items: List<String>) {
    Div(attrs = {
        style {
            property("display", "flex")
            property("flex-wrap", "wrap")
            property("width", "100%")
            property("max-width", "100%")
            property("min-width", "0")
            property("gap", "6px")
            property("margin-top", "4px")
            property("overflow", "hidden")
        }
    }) {
        items.forEach { item ->
            Div(attrs = {
                style {
                    property("display", "block")
                    property("max-width", "100%")
                    property("min-width", "0")
                    property("box-sizing", "border-box")
                    property("border", "1px solid #ffffff66")
                    property("border-radius", "12px")
                    property("padding", "3px 10px")
                    property("color", "#cccccc")
                    property("font-size", "10px")
                    property("line-height", "1.3")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                }
            }) {
                Text(item)
            }
        }
    }
}

// ── Sidebar : logiciel + étoiles ──────────────────────────────────────────────

@Composable
private fun SbSoftwareItem(name: String, stars: Int) {
    val filled = stars.coerceIn(0, 5)

    Div(attrs = {
        style {
            property("display", "flex")
            property("width", "100%")
            property("max-width", "100%")
            property("min-width", "0")
            property("gap", "6px")
            property("align-items", "flex-start")
            property("margin-bottom", "6px")
        }
    }) {
        Div(attrs = {
            style {
                property("width", "0")
                property("flex", "1")
                property("min-width", "0")
                property("max-width", "100%")
                property("color", WHITE)
                property("font-size", "10px")
                property("font-weight", "700")
                property("line-height", "1.3")
                property("letter-spacing", "0.5px")
                property("white-space", "normal")
                property("overflow-wrap", "anywhere")
                property("word-break", "break-word")
            }
        }) {
            Text(name)
        }

        Div(attrs = {
            style {
                property("flex-shrink", "0")
                property("white-space", "nowrap")
                property("color", BAR_FILL)
                property("font-size", "11px")
                property("letter-spacing", "1px")
            }
        }) {
            Text("★".repeat(filled) + "☆".repeat(5 - filled))
        }
    }
}

// ── Contenu principal : titre de section ──────────────────────────────────────

@Composable
private fun MainTitle2(title: String) {
    Div(attrs = {
        style {
            property("width", "100%")
            property("max-width", "100%")
            property("min-width", "0")
            property("box-sizing", "border-box")
            property("font-size", "13px")
            property("font-weight", "700")
            property("color", MAIN_BLUE)
            property("text-transform", "uppercase")
            property("letter-spacing", "1px")
            property("border-bottom", "2px solid $MAIN_BLUE")
            property("padding-bottom", "5px")
            property("margin-top", "20px")
            property("margin-bottom", "10px")
            property("white-space", "normal")
            property("overflow-wrap", "anywhere")
            property("word-break", "break-word")
        }
    }) {
        Text(title)
    }
}

// ── Contenu principal : liste à puces simple (réalisations) ───────────────────

@Composable
private fun SimpleBulletList(items: List<String>) {
    Div(attrs = {
        style {
            property("display", "flex")
            property("flex-direction", "column")
            property("width", "100%")
            property("max-width", "100%")
            property("min-width", "0")
            property("gap", "4px")
        }
    }) {
        items.forEach { item ->
            Div(attrs = {
                style {
                    property("display", "flex")
                    property("width", "100%")
                    property("max-width", "100%")
                    property("min-width", "0")
                    property("gap", "6px")
                    property("font-size", "10px")
                    property("color", TEXT_DARK)
                }
            }) {
                Span(attrs = {
                    style {
                        property("flex-shrink", "0")
                        property("margin-top", "1px")
                    }
                }) {
                    Text("•")
                }

                Div(attrs = {
                    style {
                        property("width", "0")
                        property("flex", "1")
                        property("min-width", "0")
                        property("max-width", "100%")
                        property("white-space", "normal")
                        property("overflow-wrap", "anywhere")
                        property("word-break", "break-word")
                    }
                }) {
                    HtmlInline(item)
                }
            }
        }
    }
}

// ── Contenu principal : carte référence ───────────────────────────────────────

@Composable
private fun ReferenceCard(
    ref: io.horion.cv.api.datasource.cv.Reference
) {
    Div(attrs = {
        style {
            property("width", "100%")
            property("max-width", "100%")
            property("min-width", "0")
            property("box-sizing", "border-box")
            property("overflow", "hidden")
            property("margin-bottom", "10px")
            property("padding-bottom", "8px")
            property("border-bottom", "1px solid #eeeeee")
        }
    }) {
        Div(attrs = {
            style {
                property("width", "100%")
                property("max-width", "100%")
                property("min-width", "0")
                property("font-weight", "700")
                property("font-size", "11px")
                property("color", MAIN_BLUE)
                property("white-space", "normal")
                property("overflow-wrap", "anywhere")
                property("word-break", "break-word")
            }
        }) {
            Text(ref.name)
        }

        val subtitleParts = listOfNotNull(
            ref.enterprise.takeIf { it.isNotBlank() },
            ref.city.takeIf { it.isNotBlank() }
        )

        if (subtitleParts.isNotEmpty()) {
            Div(attrs = {
                style {
                    property("width", "100%")
                    property("max-width", "100%")
                    property("min-width", "0")
                    property("font-size", "10px")
                    property("color", "#888888")
                    property("margin-top", "2px")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                }
            }) {
                Text(subtitleParts.joinToString(" · "))
            }
        }

        val contactParts = listOfNotNull(
            ref.phone.takeIf { it.isNotBlank() },
            ref.email.takeIf { it.isNotBlank() }
        )

        if (contactParts.isNotEmpty()) {
            Div(attrs = {
                style {
                    property("width", "100%")
                    property("max-width", "100%")
                    property("min-width", "0")
                    property("font-size", "10px")
                    property("color", TEXT_DARK)
                    property("margin-top", "3px")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                }
            }) {
                Text(contactParts.joinToString(" • "))
            }
        }
    }
}

// ── Contenu principal : bloc signature ────────────────────────────────────────

@Composable
private fun SignatureBlock(
    sig: io.horion.cv.api.datasource.common.Signature
) {
    Div(attrs = {
        style {
            property("display", "flex")
            property("justify-content", "flex-end")
            property("width", "100%")
            property("max-width", "100%")
            property("min-width", "0")
            property("box-sizing", "border-box")
            property("overflow", "hidden")
            property("margin-top", "24px")
        }
    }) {
        Div(attrs = {
            style {
                property("width", "100%")
                property("max-width", "100%")
                property("min-width", "0")
                property("box-sizing", "border-box")
                property("text-align", "right")
                property("white-space", "normal")
                property("overflow-wrap", "anywhere")
                property("word-break", "break-word")
            }
        }) {
            val line = listOfNotNull(
                sig.city.takeIf { it.isNotBlank() },
                sig.date.takeIf { it.isNotBlank() }
            )

            if (line.isNotEmpty()) {
                Div(attrs = {
                    style {
                        property("max-width", "100%")
                        property("font-size", "10px")
                        property("line-height", "1.3")
                        property("color", "#666666")
                        property("white-space", "normal")
                        property("overflow-wrap", "anywhere")
                        property("word-break", "break-word")
                    }
                }) {
                    Text(line.joinToString(", "))
                }
            }

            if (sig.image.isNotBlank()) {
                Img(
                    src = sig.image,
                    attrs = {
                        style {
                            property("display", "inline-block")
                            property("max-width", "100%")
                            property("height", "50px")
                            property("object-fit", "contain")
                            property("margin-top", "4px")
                        }
                    }
                )
            }
        }
    }
}

// ── Ligne timeline ─────────────────────────────────────────────────────────────

@Composable
private fun Cv2TimelineItem(
    period:   String,
    title:    String,
    subtitle: String,
    city:     String,
    bullets:  List<String>,
    isLast:   Boolean = false
) {
    Div(attrs = { style {
        property("display", "flex")
        property("gap", "12px")
        property("margin-bottom", "12px")
    }}) {

        // Période (ex: "2014 - 2019")
        Div(attrs = { style {
            property("min-width", "68px")
            property("font-size", "10px")
            property("font-weight", "700")
            property("color", SIDEBAR_BG)
            property("text-align", "right")
            property("line-height", "1.4")
            property("flex-shrink", "0")
            property("padding-top", "1px")
        }}) { Text(period) }

        Div(attrs = { style {
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "center")
            property("flex-shrink", "0")
            property("position", "relative")
            property("width", "11px")
        }}) {
            Div(attrs = { style {
                property("width", "11px")
                property("height", "11px")
                property("border-radius", "50%")
                property("background-color", SIDEBAR_BG)
                property("flex-shrink", "0")
                property("margin-top", "1px")
                property("position", "relative")
                property("z-index", "1")
            }})
            if (!isLast) {
                Div(attrs = { style {
                    property("position", "absolute")
                    property("top", "13px")
                    property("bottom", "-12px")
                    property("left", "5px")
                    property("width", "1px")
                    property("background", SIDEBAR_BG)
                }})
            }
        }

        // Contenu
        Div(attrs = { style {
            property("flex", "1")
            property("min-width", "0")
        }}) {

            // Titre du poste / diplôme
            if (title.isNotBlank()) {
                Div(attrs = { style {
                    property("font-weight", "700")
                    property("font-size", "11px")
                    property("color", MAIN_BLUE)
                    property("text-transform", "uppercase")
                    property("overflow-wrap", "break-word")
                }}) { Text(title) }
            }

            // Sous-titre
            if (subtitle.isNotBlank()) {
                Div(attrs = { style {
                    property("font-size", "10px")
                    property("color", "#888888")
                    property("margin-top", "2px")
                    property("overflow-wrap", "break-word")
                }}) { Text(subtitle) }
            }

            // Ville
            if (city.isNotBlank()) {
                Div(attrs = { style {
                    property("font-size", "10px")
                    property("color", CITY_GRAY)
                    property("margin-top", "2px")
                    property("margin-bottom", "3px")
                    property("overflow-wrap", "break-word")
                }}) { Text(city) }
            }

            // Bullets (description)
            if (bullets.isNotEmpty()) {
                Div(attrs = { style {
                    property("margin-top", "4px")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "2px")
                }}) {
                    bullets.forEach { b ->
                        Div(attrs = { style {
                            property("display", "flex")
                            property("gap", "6px")
                            property("font-size", "10px")
                            property("color", TEXT_DARK)
                        }}) {
                            Span(attrs = { style {
                                property("flex-shrink", "0")
                                property("margin-top", "1px")
                            }}) { Text("•") }
                            HtmlInline(b)
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun Cv2PersonalizedSection(
    section: io.horion.cv.api.datasource.cv.Section
) {
    val title = section.title
        .ifBlank { "RUBRIQUE PERSONNALISÉE" }

    MainTitle2(title)

    Div(attrs = {
        style {
            property("width", "100%")
            property("max-width", "100%")
            property("min-width", "0")
            property("box-sizing", "border-box")
            property("margin-top", "8px")
            property("margin-bottom", "14px")
            property("overflow", "hidden")
            property("line-height", "1.5")
        }
    }) {
        if (section.resume.isNotBlank()) {
            Div(attrs = {
                style {
                    property("width", "100%")
                    property("max-width", "100%")
                    property("min-width", "0")
                    property("font-size", "11px")
                    property("font-weight", "700")
                    property("color", MAIN_BLUE)
                    property("line-height", "1.4")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                }
            }) {
                Text(section.resume)
            }
        }

        if (
            section.started.isNotBlank() ||
            section.ended.isNotBlank()
        ) {
            Div(attrs = {
                style {
                    property("width", "100%")
                    property("max-width", "100%")
                    property("min-width", "0")
                    property("font-size", "10px")
                    property("color", "#888888")
                    property("line-height", "1.3")
                    property("margin-top", "3px")
                    property("margin-bottom", "5px")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                }
            }) {
                Text(
                    listOf(
                        section.started,
                        section.ended
                    )
                        .filter { it.isNotBlank() }
                        .joinToString(" — ")
                )
            }
        }

        if (section.description.isNotBlank()) {
            Div(attrs = {
                style {
                    property("width", "100%")
                    property("max-width", "100%")
                    property("min-width", "0")
                    property("font-size", "10px")
                    property("color", "#555555")
                    property("line-height", "1.5")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                    property("overflow", "hidden")
                }
            }) {
                HtmlBlock(section.description) {
                    property("width", "100%")
                    property("max-width", "100%")
                    property("min-width", "0")
                    property("font-size", "10px")
                    property("color", "#555555")
                    property("line-height", "1.5")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                }
            }
        }
    }
}